import React, { useState } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaCrop, FaArrowsAlt } from "react-icons/fa";

// ⬇️ helper pour /uploads/...
import { toAbsolutePhotoUrl } from "../../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

export default function NewsFullModal({ news, onClose }) {
  const cover = toAbsolutePhotoUrl(news.image_url) || FALLBACK;
  const [fit, setFit] = useState("cover");       // "cover" | "contain"
  const [objPos, setObjPos] = useState("center"); // ex. "center 15%"

  const onImgError = (e) => { if (e?.currentTarget) e.currentTarget.src = FALLBACK; };

  const onImgLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (h > w) {           // portrait détecté
      setFit("contain");   // par défaut on montre tout
      setObjPos("center 12%");
    } else {
      setFit("cover");
      setObjPos("center");
    }
  };

  const toggleFit = () => setFit((f) => (f === "cover" ? "contain" : "cover"));

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Lire l’article"
      onClick={(e) => e.currentTarget === e.target && onClose?.()}
    >
      <div
        className="w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white flex items-center justify-between">
          <h4 className="font-semibold truncate">{news.title || "Sans titre"}</h4>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="Fermer"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        {/* Media : hauteur responsive, pas d’aspectRatio rigide */}
        <div className="relative bg-slate-50 dark:bg-slate-900" style={{ height: "55vh", minHeight: 280 }}>
          <img
            src={cover}
            alt={news.title || "Illustration"}
            className={`absolute inset-0 w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
            style={{ objectPosition: objPos }}
            onLoad={onImgLoad}
            onError={onImgError}
          />

          {/* Bouton Ajuster/Recadrer */}
          <button
            type="button"
            onClick={toggleFit}
            title={fit === "contain" ? "Recadrer (remplir)" : "Ajuster (tout voir)"}
            className="absolute right-3 bottom-3 w-10 h-10 grid place-items-center rounded-full
                       bg-white/85 dark:bg-slate-900/75 ring-1 ring-slate-200 dark:ring-slate-700
                       text-slate-700 dark:text-slate-200 hover:ring-sky-300 transition"
          >
            {fit === "contain" ? <FaCrop /> : <FaArrowsAlt />}
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: "35vh", WebkitOverflowScrolling: "touch" }}>
          <p className="mb-3 text-xs text-slate-600 dark:text-slate-400">
            {news.author ? (
              <>
                Par{" "}
                <span className="font-semibold">
                  {news.author?.prenom} {news.author?.nom}
                </span>
              </>
            ) : null}
          </p>

          <div
            className="
              prose prose-slate dark:prose-invert max-w-none
              prose-img:rounded-xl prose-a:text-sky-600 dark:prose-a:text-sky-400
              prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2
              [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
            "
            dangerouslySetInnerHTML={{
              __html: news.content || '<p class="italic text-slate-500">Contenu vide.</p>',
            }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
